import React from 'react';
import { motion } from 'framer-motion';
import { Mail, MessageSquare, Building2, Users, FileText, Globe } from 'lucide-react';
export function ContactPage() {
  const contactReasons = [{
    icon: <Building2 size={24} data-id="element-318" />,
    title: 'Partnership Inquiries',
    description: 'Explore collaboration opportunities with educational institutions, employers, governments, or research organizations.'
  }, {
    icon: <Users size={24} data-id="element-319" />,
    title: 'Platform Demos',
    description: 'Request a demonstration of CJI, NCJI, or the broader SyNeural platform for your organization.'
  }, {
    icon: <FileText size={24} data-id="element-320" />,
    title: 'Research Collaboration',
    description: 'Discuss joint research initiatives, pilot programs, or academic partnerships.'
  }, {
    icon: <Globe size={24} data-id="element-321" />,
    title: 'Government & Public Sector',
    description: 'Inquire about civic resilience planning, civil service modernization, or leadership qualification frameworks.'
  }, {
    icon: <MessageSquare size={24} data-id="element-322" />,
    title: 'Media & Press',
    description: "Media inquiries, interview requests, or information about SyNeural's mission and platform."
  }, {
    icon: <Mail size={24} data-id="element-323" />,
    title: 'General Inquiries',
    description: 'Any other questions about SyNeural, our work, or how we might be able to help.'
  }];
  return <div className="min-h-screen bg-surface-bg" data-id="element-324">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-325">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-326">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-327">
            <span className="w-8 h-px bg-teal" data-id="element-328"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-329">
              Contact
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-330"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-331">
            Get in Touch
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-332">
            We're interested in hearing from organizations, institutions,
            researchers, and leaders who share our commitment to strengthening
            human capability, improving judgment quality, and building more
            resilient systems.
          </p>
        </motion.div>
      </section>

      {/* Contact Reasons */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-333">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-12 text-center" data-id="element-334">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-335">
            Reasons to Reach Out
          </h2>
          <p className="text-lg text-ink-muted max-w-2xl mx-auto" data-id="element-336">
            Whether you'We are exploring a partnership, requesting a demo, or
            have questions about our research – we'd like to hear from you.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-337">
          {contactReasons.map((reason, index) => <motion.div key={reason.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="bg-white p-6 rounded-lg border border-line" data-id="element-338">
              <div className="w-12 h-12 rounded-lg bg-teal/10 flex items-center justify-center text-teal mb-4" data-id="element-339">
                {reason.icon}
              </div>
              <h3 className="text-lg font-heading font-bold text-ink-dark mb-2" data-id="element-340">
                {reason.title}
              </h3>
              <p className="text-sm text-ink-muted leading-relaxed" data-id="element-341">
                {reason.description}
              </p>
            </motion.div>)}
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-342">
        <div className="max-w-3xl mx-auto" data-id="element-343">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-344">
            <div className="bg-white p-8 lg:p-12 rounded-xl border border-line shadow-sm" data-id="element-345">
              <h2 className="text-3xl font-heading font-bold text-ink-dark mb-6 text-center" data-id="element-346">
                Send Us a Message
              </h2>
              <p className="text-ink-muted text-center mb-10" data-id="element-347">
                Fill out the form below and we'll get back to you as soon as
                possible.
              </p>

              <form className="space-y-6" data-id="element-348">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-349">
                  <div data-id="element-350">
                    <label htmlFor="firstName" className="block text-sm font-medium text-ink-dark mb-2" data-id="element-351">
                      First Name *
                    </label>
                    <input type="text" id="firstName" name="firstName" required className="w-full px-4 py-3 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent" data-id="element-352" />
                  </div>
                  <div data-id="element-353">
                    <label htmlFor="lastName" className="block text-sm font-medium text-ink-dark mb-2" data-id="element-354">
                      Last Name *
                    </label>
                    <input type="text" id="lastName" name="lastName" required className="w-full px-4 py-3 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent" data-id="element-355" />
                  </div>
                </div>

                <div data-id="element-356">
                  <label htmlFor="email" className="block text-sm font-medium text-ink-dark mb-2" data-id="element-357">
                    Email Address *
                  </label>
                  <input type="email" id="email" name="email" required className="w-full px-4 py-3 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent" data-id="element-358" />
                </div>

                <div data-id="element-359">
                  <label htmlFor="organization" className="block text-sm font-medium text-ink-dark mb-2" data-id="element-360">
                    Organization
                  </label>
                  <input type="text" id="organization" name="organization" className="w-full px-4 py-3 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent" data-id="element-361" />
                </div>

                <div data-id="element-362">
                  <label htmlFor="inquiryType" className="block text-sm font-medium text-ink-dark mb-2" data-id="element-363">
                    Inquiry Type *
                  </label>
                  <select id="inquiryType" name="inquiryType" required className="w-full px-4 py-3 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent" data-id="element-364">
                    <option value="" data-id="element-365">Select an option</option>
                    <option value="partnership" data-id="element-366">Partnership Inquiry</option>
                    <option value="demo" data-id="element-367">Platform Demo Request</option>
                    <option value="research" data-id="element-368">Research Collaboration</option>
                    <option value="government" data-id="element-369">
                      Government & Public Sector
                    </option>
                    <option value="media" data-id="element-370">Media & Press</option>
                    <option value="general" data-id="element-371">General Inquiry</option>
                  </select>
                </div>

                <div data-id="element-372">
                  <label htmlFor="message" className="block text-sm font-medium text-ink-dark mb-2" data-id="element-373">
                    Message *
                  </label>
                  <textarea id="message" name="message" rows={6} required className="w-full px-4 py-3 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent resize-none" placeholder="Tell us about your inquiry..." data-id="element-374"></textarea>
                </div>

                <button type="submit" className="w-full bg-navy hover:bg-navy-light text-white font-medium px-8 py-4 rounded-lg transition-colors shadow-sm" data-id="element-375">
                  Send Message
                </button>

                <p className="text-xs text-ink-muted text-center" data-id="element-376">
                  * Required fields
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Additional Contact Info */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-377">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-378">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-8 text-center" data-id="element-379">
            Other Ways to Connect
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-380">
            <div className="bg-surface-muted p-8 rounded-lg border border-line text-center" data-id="element-381">
              <div className="w-14 h-14 rounded-full bg-teal/10 flex items-center justify-center text-teal mx-auto mb-4" data-id="element-382">
                <Mail size={28} data-id="element-383" />
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-2" data-id="element-384">
                Email Us Directly
              </h3>
              <p className="text-ink-muted mb-4" data-id="element-385">For general inquiries</p>
              <a href="mailto:contact@syneural.com" className="text-teal font-medium hover:underline" data-id="element-386">
                contact@syneural.com
              </a>
            </div>

            <div className="bg-surface-muted p-8 rounded-lg border border-line text-center" data-id="element-387">
              <div className="w-14 h-14 rounded-full bg-navy/10 flex items-center justify-center text-navy mx-auto mb-4" data-id="element-388">
                <Building2 size={28} data-id="element-389" />
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-2" data-id="element-390">
                Partnership Inquiries
              </h3>
              <p className="text-ink-muted mb-4" data-id="element-391">
                For institutional partnerships
              </p>
              <a href="mailto:partnerships@syneural.com" className="text-navy font-medium hover:underline" data-id="element-392">
                partnerships@syneural.com
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Response Time Notice */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-393">
        <div className="max-w-3xl mx-auto text-center" data-id="element-394">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-395">
            <h3 className="text-2xl font-heading font-bold mb-4" data-id="element-396">
              We'll Get Back to You Soon
            </h3>
            <p className="text-lg opacity-90" data-id="element-397">
              We typically respond to inquiries within 2-3 business days. For
              urgent matters, please indicate that in your message.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}